<script setup lang="ts">
  import { computed, ref, watchEffect } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { fmt } from '@/functions'

  const route = useRoute()
  const router = useRouter()

  const props = defineProps<{
    data?: Pagination,
  }>()

  const currentPage = ref<number>(Number(route.query.page ?? 1))
  const lastPage = computed(() => props.data?.last_page ?? 1)
  const firstPageLink = computed(() => ({ ...route, query: { ...route.query, page: 1 } }))
  const lastPageLink = computed(() => ({ ...route, query: { ...route.query, page: lastPage.value } }))
  const prevLink = computed(() => {
    if (currentPage.value <= 1) {
      return
    }
    const prevPage = currentPage.value === 2 ? undefined : currentPage.value - 1
    return { ...route, query: { ...route.query, page: prevPage } }
  })
  const nextLink = computed(() => {
    if (currentPage.value >= lastPage.value) {
      return
    }
    return { ...route, query: { ...route.query, page: currentPage.value + 1 } }
  })

  const sliceSize = 7
  const slicedPages = computed(() => {
    if (!currentPage.value || !lastPage.value) {
      return [1]
    }

    if (lastPage.value <= sliceSize) {
      return Array.from({ length: lastPage.value }, (_, i) => i + 1)
    }

    const result = [currentPage.value]
    while (result.length < sliceSize) {
      if (result[0]! > 1) {
        result.unshift(result[0]! - 1)
      }
      if (result[result.length - 1]! < lastPage.value) {
        result.push(result[result.length - 1]! + 1)
      }
    }

    return result
  })

  watchEffect(() => {
    const newPage = currentPage.value === 1 ? undefined : currentPage.value
    router.push({ ...route, query: { ...route.query, page: newPage } })
  })
  watchEffect(() => {
    currentPage.value = Number(route.query.page ?? 1)
    // window.scrollTo({ top: 0 })
  })

  const defaultPerPage = 50
  const perPage = ref<number>(Number(route.query.per_page ?? defaultPerPage))
  const perPageOptions = [50, 100, 250, 500, 1000]
  watchEffect(() => {
    router.push({
      ...route,
      query: {
        ...route.query,
        page: undefined,
        per_page: perPage.value === defaultPerPage ? undefined : perPage.value,
      },
    })
  })
</script>

<template>
  <nav
    v-if="data && lastPage > 1"
    role="navigation"
    aria-label="Pagination Navigation">
    <div class="flex items-center gap-y-2 max-lg:flex-col lg:items-end lg:justify-between pt-8 pb-4">
      <div class="py-2">
        <div class="flex items-center space-x-2 mb-2">
          <div>Tampilkan</div>
          <select
            v-model="perPage"
            class="text-xs border h-7">
            <option
              v-for="opt in perPageOptions"
              :key="opt"
              :value="opt">
              {{ opt }}
            </option>
          </select>
          <div>per halaman</div>
        </div>

        <div class="text-xs lg:text-sm">
          Menampilkan
          <span class="font-semibold">{{ fmt.number(data.from) }}-{{ fmt.number(data.to) }}</span>
          dari
          <span class="font-semibold">{{ fmt.number(data.total) }}</span>
        </div>
      </div>

      <div class="flex-center text-sm gap-2 flex-wrap">
        <router-link
          v-if="currentPage > 1"
          :to="firstPageLink"
          aria-label="Halaman Pertama"
          class="pagination-link">
          <!-- &lsaquo; -->
          <fa-icon icon="i-far-angles-left" />
        </router-link>
        <router-link
          v-if="prevLink"
          :to="prevLink"
          aria-label="Halaman Sebelumnya"
          class="pagination-link">
          <!-- &lsaquo; -->
          <fa-icon icon="i-far-angle-left" />
        </router-link>
        <router-link
          v-for="i in slicedPages"
          :key="i"
          :to="{query:{page: i, per_page: route.query.per_page}}"
          class="pagination-link"
          :class="{ 'pagination-link-active': i === currentPage }">
          {{ i }}
        </router-link>
        <router-link
          v-if="nextLink"
          :to="nextLink"
          aria-label="Halaman Berikutnya"
          class="pagination-link">
          <!-- &rsaquo; -->
          <fa-icon icon="i-far-angle-right" />
        </router-link>
        <router-link
          v-if="currentPage < lastPage"
          :to="lastPageLink"
          aria-label="Halaman Terakhir"
          class="pagination-link">
          <!-- &raquo; -->
          <fa-icon icon="i-far-angles-right" />
        </router-link>
      </div>
    </div>
  </nav>
  <div v-else></div>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .pagination-link {
    @apply flex-center py-2 text-sm lg:text-base min-w-4 lg:min-w-5;
  }
  .pagination-link-active {
    @apply font-bold underline;
  }
</style>
